// Hồi Sơn (UI_SPEC §16): vỏ số 5 ở `--z-ceremony`. Chỉ ĐỌC bản kê của lần quyết toán vừa rồi.
// Một số chính = quãng vắng. Chạm bất kỳ đâu để vào. Ruột dùng chung với trang con Thân.
import type { JSX } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { t } from '@i18n/t';
import { formatFull } from '@i18n/format';
import { CANH_MS, elapsedText } from '@i18n/time';
import { stageLabel } from '@i18n/stage';
import { dismissReturn, qiIntoBar, returnSummary, type ReturnSummary } from '@state/modules/returnSummary';

/** Tối đa 4 dòng Thế Sự; nhiều hơn thì dòng cuối "…và N chuyện khác". */
const WORLD_LINES = 4;

export function ReturnBody({ s, compact }: { s: ReturnSummary; compact?: boolean }): JSX.Element {
  const r = s.report;
  const world = [
    ...r.worldNews,
    ...(r.deathSeclusionEndedAtMs !== null ? ['log.cult.deathSeclusionEnd'] : []),
    ...r.rareLines,
  ];
  const shown = world.length > WORLD_LINES ? world.slice(0, WORLD_LINES - 1) : world;
  // Phần vắng quá trần: chip và dòng giải thích dùng cùng một số (làm tròn lên).
  const cappedWatches = Math.ceil(r.cappedMs / CANH_MS);
  return (
    <>
      {!compact && (
        <>
          <b class="ret-hero" data-id="return-absent">
            {elapsedText(s.absentMs)}
          </b>
          <small class="ret-sub">{t('ui.return.sub')}</small>
          {r.cappedMs > 0 && (
            <span class="ht-chip warn" data-id="return-capped">
              {t('ui.return.capped', { n: cappedWatches })}
            </span>
          )}
        </>
      )}
      <div class="ret-block" data-id="return-harvest">
        <em>{t('ui.return.harvest')}</em>
        <div class="return-line">
          <span>{t('cult.cultivationXp')}</span>
          <b data-id="return-qi">+{formatFull(Math.floor(qiIntoBar(r)))}</b>
        </div>
        {s.stepAfter > s.stepBefore && (
          <div class="return-line">
            <span>{t('ui.return.stagesLabel')}</span>
            <b data-id="return-stages">
              {t('ui.return.stages', { from: stageLabel(s.stepBefore), to: stageLabel(s.stepAfter) })}
            </b>
          </div>
        )}
        <div class="return-line">
          <span>{t('cult.mastery')}</span>
          <b>+{formatFull(Math.floor(r.masteryGained))}</b>
        </div>
        {s.trial && (
          <div class="return-line" data-id="return-trial">
            <span>{t('ui.return.trial')}</span>
            <b>
              {t('ui.return.trialLine', {
                k: formatFull(s.trial.kills),
                i: formatFull(s.trial.items),
                s: formatFull(s.trial.stones),
              })}
            </b>
          </div>
        )}
        {s.trial && s.trial.equipped > 0 && (
          <p class="return-line dim" data-id="return-equipped">
            {t('ui.return.trialEquip', { n: s.trial.equipped })}
          </p>
        )}
        {s.trial && s.trial.salvaged > s.trial.salvagedFull && (
          <p class="return-line dim" data-id="return-salvaged">
            {t('ui.return.trialSalvage', { n: s.trial.salvaged - s.trial.salvagedFull })}
          </p>
        )}
        {s.trial && s.trial.salvagedFull > 0 && (
          <p class="return-line dim" data-id="return-salvaged-full">
            {t('ui.return.trialSalvageFull', { n: s.trial.salvagedFull })}
          </p>
        )}
        {s.trial && s.trial.sealed > 0 && (
          <p class="return-line">{t('ui.return.sealed', { n: s.trial.sealed })}</p>
        )}
        {r.overflowGained > 0 && (
          <div class="return-line">
            <span>{t('cult.overflowQi')}</span>
            <b>+{formatFull(Math.floor(r.overflowGained))}</b>
          </div>
        )}
      </div>
      <div class="ret-block" data-id="return-world">
        <em>{t('ui.return.world')}</em>
        <p class="ret-while">{t('ui.return.while')}</p>
        {shown.length === 0 && <p class="return-line quiet">{t('ui.return.quiet')}</p>}
        {shown.map((k, i) => (
          <p class="return-line" key={`${k}-${i}`}>
            {t(k)}
          </p>
        ))}
        {world.length > shown.length && (
          <p class="return-line">{t('ui.return.more', { n: world.length - shown.length })}</p>
        )}
        {r.wastedQi >= 1 && (
          <p class="return-line dim" data-id="return-wasted">
            {t('ui.return.wasted', { n: formatFull(Math.floor(r.wastedQi)) })}
          </p>
        )}
        {cappedWatches > 0 && (
          <p class="return-line dim">{t('ui.return.cappedLine', { n: cappedWatches })}</p>
        )}
      </div>
    </>
  );
}

export function ReturnHost(): JSX.Element | null {
  const s = returnSummary.value;
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (s) ref.current?.focus();
  }, [s]);
  if (!s) return null;
  return (
    <section
      ref={ref}
      class="ht-return"
      role="dialog"
      aria-modal="true"
      aria-label={t('ui.return.title')}
      data-id="return"
      tabIndex={-1}
      onClick={dismissReturn}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
          e.preventDefault();
          dismissReturn();
        }
      }}
    >
      <h2 class="ret-title">{t('ui.return.title')}</h2>
      <ReturnBody s={s} />
      <small class="ret-tap">{t('ui.return.tap')}</small>
    </section>
  );
}
